import type { ChatUiHotelOffer } from '../hotel_planner/hotel_planner_schema.js';
import {
  hotelCancellationWindows,
  hotelFreeCancellationUntil,
} from '../shared/cancellation_policy.js';
import type { ChatUiHotelAmenity } from '../shared/hotel_amenity_schema.js';
import { hotelPhotosToChatUi } from '../shared/hotel_photo_schema.js';
import { nightsFromStay } from '../shared/nights.js';
import { genUiT, resolveGenUiLocale } from '../../shared/i18n_core.js';
import { utf16UnitAt } from '../../shared/utf16_unit.js';
import { roomIdentityLabel } from './hotel_room_identity_label.js';
import {
  classifyRoomName,
  roomIdentityKey,
  type RoomClassification,
  type RoomIdentity,
} from './hotel_room_taxonomy.js';
import {
  HotelRoomsBlockSchema,
  type HotelRoomsBlock,
  type HotelRoomsRateFilters,
} from './hotel_rooms_schema.js';

export type BuildHotelRoomsInput = Omit<HotelRoomsBlock, 'id' | 'kind'>;

export function buildHotelRooms(input: BuildHotelRoomsInput, id = 'hotel_rooms') {
  return HotelRoomsBlockSchema.parse({ id, kind: 'hotel_rooms', ...input });
}

/**
 * Um quarto na entrada da projeção. Duas formas do MESMO quarto chegam aqui, e o
 * tipo aceita as duas de propósito para que UMA função sirva os dois donos da
 * escolha: o resumo CRU do provider (que o agents guarda em estado e usa no
 * checkout) e o recorte FINO que o `hotel_planner` levou ao browser
 * (`ChatUiHotelOffer.rooms`, de onde o drawer de detalhes monta o card). Cada par
 * cru/pronto — dinheiro, fornecedor, cancelamento — é resolvido em `rateFromRoom`,
 * preferindo o valor já pronto. O tipo é estrutural: nenhum dos dois lados
 * precisa conhecer o outro.
 */
export interface HotelRoomsSourceRoom {
  description?: string;
  name?: string;
  about?: string;
  boardDescription?: string;
  refundable?: boolean;
  breakfast?: boolean;
  photos?: { url?: string; description?: string }[];
  amenities?: ChatUiHotelAmenity[];
  policyStatus?: 'within_policy' | 'out_of_policy' | 'unknown';
  /** As regras quebradas por esta tarifa (`HotelRoomSource.policyViolations`) —
   *  o motivo que acompanha o veredito. Vazio é ausente. */
  policyViolations?: string[];
  quantity?: number;
  negotiated?: boolean;
  /* Já pronto (forma do wire) */
  total?: { amount: number; currency: string };
  totalPerNight?: { amount: number; currency: string };
  supplier?: { name?: string; image?: string; icon?: string };
  freeCancellationUntil?: string;
  /* Cru (forma do provider) */
  totalAmount?: number;
  totalPerNightAmount?: number;
  currency?: string;
  supplierName?: string;
  supplierImage?: string;
  supplierIcon?: string;
  cancellationPolicies?: unknown[];
}

/** A oferta de hotel de onde a projeção tira cabeçalho e quartos (estrutural,
 *  mesma razão de `HotelRoomsSourceRoom`). */
export interface HotelRoomsSourceOffer {
  offerId: string;
  title: string;
  rating?: number;
  reviewCount?: number;
  address?: string;
  photos?: { url?: string; description?: string }[];
  rooms?: HotelRoomsSourceRoom[];
  /** Tokens residuais julgados ruído pelo dicionário da fase 2 — suprimidos do
   *  qualifier raw ANTES da chave. Ausente/vazio = comportamento da fase 1. */
  noiseTokens?: readonly string[];
}

/** A estadia do turno: datas + ocupação por quarto, o suficiente para a linha de
 *  cabeçalho do card. */
export interface HotelRoomsStay {
  checkin?: string;
  checkout?: string;
  /** `readonly` porque a estadia em cena chega do `drawerStay`, que devolve a
   *  ocupação do bloco sem copiá-la — a projeção só lê. */
  rooms?: readonly { guests: number }[];
}

interface BuildHotelRoomsOptions {
  selectedRateRoomIndex?: number;
  viewOnly?: boolean;
  /** A oferta escolhida, para o card que abre o turno. Os `rooms` dela são
   *  descartados: os quartos deste bloco são a projeção completa, e repetir a
   *  lista dobraria o payload sem nada a mais na tela. */
  offer?: ChatUiHotelOffer;
  /** #3827: os chips de tarifa que o cartão abre ligados — o recorte que o
   *  agente aplicou pelo pedido do viajante. */
  rateFilters?: HotelRoomsRateFilters;
  /** M1 · 1.9 — número da emissão da LISTA viva deste hotel no turno
   *  (`HotelState.roomsBlockSeq`): o id vira `hotel_rooms:<h(offerId)>:<seq>`,
   *  e cada reabertura nasce como card NOVO na linha da resposta em vez de
   *  reescrever a lista lá em cima (P12: 5 de 7 conversas com toque repetido
   *  morreram ali). */
  seq?: number;
  /** Id EXPLÍCITO — o recibo congelado (`viewOnly`), o aviso de tarifa e o
   *  card vivo rearmado miram o id da lista viva (`HotelState.liveRoomsBlockId`,
   *  invariante do recibo #1936). Vence `seq`. */
  id?: string;
  /** Locale da sessão (hint cru; `resolveGenUiLocale` normaliza). Ausente ⇒ pt-BR. */
  locale?: string | null;
}

const MAX_GROUP_PHOTOS = 8;

type HotelRoomsGroup = HotelRoomsBlock['rooms'][number];
type HotelRoomsRate = HotelRoomsGroup['rates'][number];

function text(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function bounded(value: number | undefined, min: number, max: number): number | undefined {
  return value !== undefined && Number.isFinite(value) && value >= min && value <= max
    ? value
    : undefined;
}

/** Only a finite non-negative integer survives; anything else (negative,
 *  fractional, NaN, Infinity) becomes undefined so `withoutUndefined` drops
 *  it — the schema requires `z.number().int().min(0)` and a raw provider
 *  value that violates it would otherwise fail the whole block's parse. */
function nonNegativeInt(value: number | undefined): number | undefined {
  return value !== undefined && Number.isInteger(value) && value >= 0 ? value : undefined;
}

function withoutUndefined<T extends Record<string, unknown>>(values: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

/** A data da estadia só entra no bloco quando é ISO de verdade. O `hotel_planner`
 *  as carrega TOLERANTES (semente do turno anterior, possivelmente malformada) e
 *  o schema do card as exige `iso.date()` — sem este filtro uma data ruim faria o
 *  parse rejeitar o bloco INTEIRO, e a escolha do quarto desapareceria junto com
 *  a linha de estadia. */
function isoDate(value: string | undefined): string | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === value
    ? value
    : undefined;
}

function amenitiesToChatUi(
  amenities: ChatUiHotelAmenity[] | undefined,
): ChatUiHotelAmenity[] | undefined {
  if (!amenities?.length) return undefined;
  const mapped = amenities
    .map((amenity) => {
      const label = text(amenity.label);
      return label ? { ...(amenity.code ? { code: amenity.code } : {}), label } : undefined;
    })
    .filter((amenity): amenity is ChatUiHotelAmenity => amenity !== undefined)
    .slice(0, 24);
  return mapped.length > 0 ? mapped : undefined;
}

/** O prazo de cancelamento grátis da tarifa, na ordem que importa.
 *
 *  A não-reembolsável corta ANTES do campo já pronto: bloco antigo em replay
 *  carrega o `freeCancellationUntil` da conta velha (a que lia o FIM da janela
 *  de multa), e um `??` deixaria esse valor passar por cima da cerca. Com
 *  política em mãos, a janela manda; o campo pronto é só o último recurso. */
function freeCancellationFromRoom(room: HotelRoomsSourceRoom): string | undefined {
  if (room.refundable === false) return undefined;
  const windows = hotelCancellationWindows(room.cancellationPolicies);
  return windows.length > 0
    ? hotelFreeCancellationUntil(windows, room.refundable)
    : room.freeCancellationUntil;
}

/** O veredito da política DESTA tarifa mais o motivo dele — juntos porque o
 *  motivo sem o veredito não se lê, e separados de `rateFromRoom` para a
 *  projeção caber no teto de complexidade. Lista vazia = ausente. */
function policyOfRate(
  room: HotelRoomsSourceRoom,
): Pick<HotelRoomsRate, 'policyStatus' | 'policyViolations'> {
  return {
    ...(room.policyStatus ? { policyStatus: room.policyStatus } : {}),
    ...(room.policyViolations?.length ? { policyViolations: room.policyViolations } : {}),
  };
}

/** One rate row per raw provider room summary, including the hotel_rooms-only
 *  fields (`negotiated`/`policyStatus`/`quantity`/`totalPerNight`). `roomIndex`
 *  is the room's 1-based position in the offer's ORIGINAL flat `rooms` array —
 *  the identity `select_room`/`capturePricing` consume downstream. */
function rateFromRoom(room: HotelRoomsSourceRoom, roomIndex: number): HotelRoomsRate {
  const currency = text(room.currency);
  const money = (amount: number | undefined) =>
    amount !== undefined && Number.isFinite(amount) && currency ? { amount, currency } : undefined;
  const supplier =
    room.supplier ??
    withoutUndefined({
      name: text(room.supplierName),
      image: text(room.supplierImage),
      icon: text(room.supplierIcon),
    });
  const total = room.total ?? money(room.totalAmount);
  const totalPerNight = room.totalPerNight ?? money(room.totalPerNightAmount);
  const freeCancellation = freeCancellationFromRoom(room);
  return {
    roomIndex,
    ...(Object.keys(supplier).length > 0 ? { supplier } : {}),
    ...(text(room.boardDescription) ? { boardDescription: text(room.boardDescription) } : {}),
    ...(room.breakfast === undefined ? {} : { breakfast: room.breakfast }),
    ...(room.refundable === undefined ? {} : { refundable: room.refundable }),
    ...(freeCancellation ? { freeCancellationUntil: freeCancellation } : {}),
    ...(total ? { total } : {}),
    ...(totalPerNight ? { totalPerNight } : {}),
    ...policyOfRate(room),
    ...(room.negotiated ? { negotiated: true } : {}),
    ...(room.quantity === undefined ? {} : { quantity: room.quantity }),
    ...(rawRoomName(room) ? { sourceName: rawRoomName(room) } : {}),
  };
}

/** O texto cru que identifica o quarto para o VIAJANTE (description > name). */
function rawRoomName(room: HotelRoomsSourceRoom): string | undefined {
  return text(room.description) ?? text(room.name);
}

/** `true` quando `identity` cabe nos caps do wire (`hotel_rooms_schema.ts`:
 *  cama 1–9, capacidade 1–19, até 4 camas, até 12 qualificadores, `raw` até 64
 *  chars) EXATAMENTE como `classifyRoomName` a produziu — nunca corta pra
 *  caber. Cortar (clamp/slice/truncate) FUNDE quartos genuinamente diferentes
 *  que só concordam ATÉ o corte: dois nomes iguais nos 12 primeiros tokens de
 *  resíduo e diferentes no 13º caem na MESMA chave depois de um
 *  `slice(0, 12)`; dois nomes com "10 CAMAS"/"11 CAMAS" perdem a cama do
 *  mesmo jeito e fundem no resto. Essa fusão silenciosa é a violação exata da
 *  assimetria do design — dividir é seguro, fundir errado não é. */
function fitsWireIdentityCaps(identity: RoomIdentity): boolean {
  const beds = identity.beds ?? [];
  const qualifiers = identity.qualifiers ?? [];
  const bedroomsFit =
    identity.bedrooms === undefined || (identity.bedrooms >= 1 && identity.bedrooms <= 9);
  const capacityFits =
    identity.capacity === undefined || (identity.capacity >= 1 && identity.capacity <= 19);
  const bedsFit = beds.length <= 4 && beds.every((bed) => bed.count >= 1 && bed.count <= 9);
  const qualifiersFit =
    qualifiers.length <= 12 && qualifiers.every((qualifier) => (qualifier.raw?.length ?? 0) <= 64);
  return bedroomsFit && capacityFits && bedsFit && qualifiersFit;
}

/** Roda logo após `classifyRoomName`, ANTES de qualquer chave/rótulo. Uma
 *  identity que NÃO cabe nos caps do wire (`fitsWireIdentityCaps`) degrada pra
 *  `illegible` em vez de ser cortada — sem isso, um nome patológico faria o
 *  `HotelRoomsBlockSchema.parse` do BLOCO INTEIRO falhar (`buildHotelRoomsBlock`
 *  devolve `null`, apagando os OUTROS quartos saudáveis do mesmo hotel).
 *  Illegible cai no agrupamento por string exata: divide sempre, nunca funde
 *  o que só parece igual depois de cortado. */
function wireSafeClassification(classification: RoomClassification): RoomClassification {
  if (classification.kind !== 'classified') return classification;
  return fitsWireIdentityCaps(classification.identity) ? classification : { kind: 'illegible' };
}

interface ClassifiedEntry {
  room: HotelRoomsSourceRoom;
  rate: HotelRoomsRate;
  classification: RoomClassification;
}

/** R5: cama (n≥2, família desconhecida) adota a família do ÚNICO vizinho de
 *  mesmo hotel idêntico em todos os DEMAIS eixos com (n, família conhecida).
 *  Dois candidatos de famílias distintas ⇒ não adota (escolher inventaria um
 *  fato). Muta a identity da entrada — roda antes do agrupamento. */
function absorbUnknownBedFamilies(entries: ClassifiedEntry[]): void {
  const classified = entries.filter(
    (
      entry,
    ): entry is ClassifiedEntry & {
      classification: Extract<RoomClassification, { kind: 'classified' }>;
    } => entry.classification.kind === 'classified',
  );
  // identidade SEM as camas: o filtro da absorção são os DEMAIS eixos
  const axesKey = (identity: RoomIdentity) => roomIdentityKey({ ...identity, beds: [] });
  for (const entry of classified) {
    const beds = entry.classification.identity.beds ?? [];
    const unknown = beds.find((bed) => bed.family === undefined && bed.count >= 2);
    if (!unknown) continue;
    const families = new Set(
      classified
        .filter((candidate) => candidate !== entry)
        .filter(
          (candidate) =>
            axesKey(candidate.classification.identity) === axesKey(entry.classification.identity),
        )
        .flatMap((candidate) => candidate.classification.identity.beds ?? [])
        .filter((bed) => bed.count === unknown.count && bed.family !== undefined)
        .map((bed) => bed.family),
    );
    if (families.size === 1) unknown.family = [...families][0];
  }
}

function groupKeyOf(entry: ClassifiedEntry): string {
  if (entry.classification.kind === 'classified') {
    return `id:${roomIdentityKey(entry.classification.identity)}`;
  }
  // ilegível/placeholder agrupa pela string exata, como hoje — e vai pro porão.
  // Sem raw nenhuma (nem description nem name), a chave é única por posição:
  // um fallback fixo colidiria com um quarto REAL cujo nome seja literalmente
  // 'Quarto', fundindo dois quartos-sem-informação distintos (dividir>fundir).
  const raw = rawRoomName(entry.room);
  return raw === undefined ? `raw:#${String(entry.rate.roomIndex)}` : `raw:${raw}`;
}

function newRoomGroup(entry: ClassifiedEntry, locale: string | null | undefined): HotelRoomsGroup {
  const { room, rate, classification } = entry;
  const photos = hotelPhotosToChatUi(room.photos, MAX_GROUP_PHOTOS);
  const amenities = amenitiesToChatUi(room.amenities);
  const classified = classification.kind === 'classified';
  const name = classified
    ? roomIdentityLabel(classification.identity, resolveGenUiLocale(locale))
    : (rawRoomName(room) ?? genUiT(locale, 'roomTaxonomy.type.QUARTO'));
  return {
    name,
    ...(classified ? { identity: classification.identity } : { unclassified: true }),
    ...(text(room.about) ? { about: text(room.about) } : {}),
    ...(photos ? { photos } : {}),
    ...(amenities ? { amenities } : {}),
    rates: [rate],
  };
}

/** Backfills a group's photos/amenities from a later room in the SAME group
 *  when the group doesn't have its own yet (first-room-wins). Coleta TODAS as
 *  tarifas — nenhuma é descartada em nenhum ponto do caminho. */
function mergeIntoGroup(
  group: HotelRoomsGroup,
  room: HotelRoomsSourceRoom,
  rate: HotelRoomsRate,
): void {
  group.rates.push(rate);
  if (!group.photos) {
    const photos = hotelPhotosToChatUi(room.photos, MAX_GROUP_PHOTOS);
    if (photos) group.photos = photos;
  }
  if (!group.amenities) {
    const amenities = amenitiesToChatUi(room.amenities);
    if (amenities) group.amenities = amenities;
  }
  if (!group.about) {
    const about = text(room.about);
    if (about) group.about = about;
  }
}

/** Groups the offer's flat provider rooms by canonical identity (6 eixos da
 *  taxonomia) for the `hotel_rooms` card, keeping each rate's ORIGINAL
 *  1-based index into `offer.rooms` (see `rateFromRoom`). Ilegível/placeholder
 *  cai de volta ao agrupamento por string exata, como antes.
 *
 *  SEM TETO, nem de grupos nem de tarifas por grupo: agrupar só pode
 *  ENRIQUECER. Os tetos que existiam aqui (24 grupos, 10 tarifas) descartavam
 *  a cauda em SILÊNCIO — o excedente de grupos não caía nem no porão —, e o
 *  agrupamento nasceu justamente para o viajante comparar preço entre
 *  fornecedores. O par no schema (`hotel_rooms_schema.ts`) também não tem
 *  teto, então nada aqui precisa truncar para o parse passar. */
function groupRooms(
  offer: HotelRoomsSourceOffer,
  locale: string | null | undefined,
): HotelRoomsGroup[] {
  const learnedNoise = offer.noiseTokens?.length ? new Set(offer.noiseTokens) : undefined;
  const entries: ClassifiedEntry[] = (offer.rooms ?? []).map((room, index) => ({
    room,
    rate: rateFromRoom(room, index + 1),
    classification: wireSafeClassification(classifyRoomName(rawRoomName(room), learnedNoise)),
  }));
  absorbUnknownBedFamilies(entries);
  const groups = new Map<string, HotelRoomsGroup>();
  entries.forEach((entry) => {
    const key = groupKeyOf(entry);
    const existing = groups.get(key);
    if (existing) {
      mergeIntoGroup(existing, entry.room, entry.rate);
      return;
    }
    groups.set(key, newRoomGroup(entry, locale));
  });
  return [...groups.values()];
}

/** Property-level photo fallback for a group with none of its own — never
 *  applied when the group already has ANY photo, even from a different room
 *  in the same group. */
function withPropertyPhotoFallback(
  groups: HotelRoomsGroup[],
  offer: HotelRoomsSourceOffer,
): HotelRoomsGroup[] {
  const propertyPhotos = hotelPhotosToChatUi(offer.photos, MAX_GROUP_PHOTOS);
  if (!propertyPhotos) return groups;
  return groups.map((group) => (group.photos ? group : { ...group, photos: propertyPhotos }));
}

function hotelRoomsHeader(offer: HotelRoomsSourceOffer): HotelRoomsBlock['hotel'] | null {
  const offerId = text(offer.offerId);
  const title = text(offer.title);
  if (!offerId || !title) return null;
  return {
    offerId,
    title,
    ...withoutUndefined({
      rating: bounded(offer.rating, 0, 10),
      reviewCount: nonNegativeInt(offer.reviewCount),
      address: text(offer.address),
    }),
  };
}

/** O card do turno mostra o hotel, não os quartos — a lista sai fora. */
function offerCard(offer: ChatUiHotelOffer): ChatUiHotelOffer {
  const card = { ...offer };
  delete card.rooms;
  return card;
}

function hotelRoomsGuestsCount(stay: HotelRoomsStay): number | undefined {
  const guests = stay.rooms?.reduce((sum, room) => sum + room.guests, 0);
  return guests || undefined;
}

/** Id do bloco ancorado na OFERTA, não numa constante.
 *
 *  `hotel_rooms` reconcilia por id, e a reconciliação do web-chat ancora o bloco
 *  na PRIMEIRA mensagem em que ele apareceu. Com o id fixo `hotel_rooms`,
 *  escolher um SEGUNDO hotel na mesma conversa reescrevia o card lá em cima e o
 *  turno atual ficava só com a fala ("Ótima escolha. Agora vamos escolher o
 *  quarto.") — beco sem saída, porque o viajante está olhando o rodapé.
 *
 *  Por oferta é o escopo certo: o congelamento `viewOnly` do checkout continua
 *  reconciliando sobre o card vivo do MESMO hotel, e um hotel novo ancora um
 *  bloco novo. Mesma régua do `hotel_planner`, que já minta id por busca.
 *
 *  O offerId entra HASHEADO, não cru: `chatUiBase.id` é `max(64)` e o handle do
 *  modal-session é opaco e longo — interpolá-lo direto estourava o teto, o
 *  `parse` jogava e o builder devolvia `null`, derrubando o card inteiro para o
 *  texto de fallback. Truncar não serve (handles opacos compartilham prefixo);
 *  o hash é curto, determinístico e estável por oferta, que é o que a
 *  reconciliação pede. */
function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= utf16UnitAt(value, i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function hotelRoomsBlockId(offerId: string, seq?: number): string {
  return `hotel_rooms:${fnv1a(offerId)}${seq === undefined ? '' : `:${String(seq)}`}`;
}

/** Os campos do bloco que só existem quando a opção veio — fora de
 *  `buildHotelRoomsBlock` pelo teto de complexidade. */
function optionalBlockFields(options: BuildHotelRoomsOptions): Partial<BuildHotelRoomsInput> {
  const { selectedRateRoomIndex, rateFilters } = options;
  return {
    ...(selectedRateRoomIndex ? { selectedRateRoomIndex } : {}),
    ...(rateFilters && Object.keys(rateFilters).length > 0 ? { rateFilters } : {}),
    ...(options.viewOnly ? { viewOnly: true } : {}),
    ...(options.offer ? { offer: offerCard(options.offer) } : {}),
  };
}

/**
 * `hotel_rooms` block — room choice for an already-picked hotel (the offer's
 * flat `rooms` grouped by canonical identity, see `groupRooms`). Pure
 * projection over an already hydrated offer; returns null (fail-soft) when
 * the offer has no rooms or the parse fails, so callers skip
 * emitting/rendering the card entirely.
 */
export function buildHotelRoomsBlock(
  offer: HotelRoomsSourceOffer,
  stay: HotelRoomsStay,
  options: BuildHotelRoomsOptions = {},
): HotelRoomsBlock | null {
  const hotel = hotelRoomsHeader(offer);
  if (!hotel) return null;
  const rooms = withPropertyPhotoFallback(groupRooms(offer, options.locale), offer);
  if (rooms.length === 0) return null;
  const blockId = options.id ?? hotelRoomsBlockId(hotel.offerId, options.seq);
  const checkin = isoDate(stay.checkin);
  const checkout = isoDate(stay.checkout);
  const nights = nightsFromStay(checkin, checkout);
  const guestsCount = hotelRoomsGuestsCount(stay);
  try {
    return buildHotelRooms(
      {
        hotel,
        rooms,
        ...(checkin ? { checkin } : {}),
        ...(checkout ? { checkout } : {}),
        ...(nights ? { nights } : {}),
        ...(stay.rooms?.length ? { roomsCount: stay.rooms.length } : {}),
        ...(guestsCount ? { guestsCount } : {}),
        ...optionalBlockFields(options),
      },
      blockId,
    );
  } catch {
    return null;
  }
}
